import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import {
	TextField,
	Checkbox,
	FormControlLabel,
	Button,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	IconButton,
	SelectChangeEvent,
	Switch,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; //
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { getAllCategory } from '../../../services/api/Category';
import { addProduct, deleteProductImage, updateProduct } from '../../../services/api/ProductAPI';
// import { buldDiscount, FormData, wholesale } from '../../../../types/product';

// Remove this interface as it conflicts with the imported type

interface wholesale {
	type: 'text' | 'image';
	label: string;
	required: boolean;
}

interface buldDiscount {
	minQuantity: number;
	maxQuantity: number;
	discountPercentage: number;
}

interface FormData {
	_id: string;
	name: string;
	category: string;
	description: string;
	price: string;
	quantity: string;
	bestSelling: boolean;
	trending: boolean;
	productImage: string[];
	weight: string;
	weightUnit: string;
	isInquiry: boolean;
	isBulkDiscount: boolean;
	isCustomized: boolean;
	slug?: string;
	isWholesale: boolean;
	wholesaleFields: wholesale[];
	buldDiscountsData: buldDiscount[];
	wholesaleMinQuantity: number;
	productId: string;
}
interface CreateProductsProps {
	product?: FormData | null;
	fetchData: () => Promise<void>;
	openDialog: () => void;
}
const CreateProducts: React.FC<CreateProductsProps> = ({ product, fetchData, openDialog }) => {
	const [formData, setFormData] = useState<FormData>({
		_id: product?._id || '',
		name: product?.name || '',
		description: product?.description || '',
		category: product?.category || '',
		price: product?.price || '',
		quantity: product?.quantity || '',
		bestSelling: product?.bestSelling || false,
		trending: product?.trending || false,
		productImage: product?.productImage || [],
		weight: product?.weight || '',
		weightUnit: product?.weightUnit || 'kg',
		slug: product?.slug || '',
		isInquiry: product?.isInquiry || false,
		isBulkDiscount: product?.isBulkDiscount || false,
		isCustomized: product?.isCustomized || false,
		isWholesale: product?.isWholesale || false,
		wholesaleFields: product?.wholesaleFields || [],
		buldDiscountsData: product?.buldDiscountsData || [],
		wholesaleMinQuantity: product?.wholesaleMinQuantity || 1,
		productId: product?.productId || '',
	});
	const [newProductImage, setNewProductImage] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);
	const [customizedField, setCustomizedField] = useState<wholesale>({
		type: 'text',
		label: '',
		required: false,
	});
	const [error, setError] = useState({
		customizedLabelError: false, // Separate error state
	});
	const [bulkDiscountField, setBulkDiscountField] = useState<buldDiscount>({
		minQuantity: 1,
		maxQuantity: 1,
		discountPercentage: 1,
	});

	const [imagePreviews, setImagePreviews] = useState<string[]>([]);
	const [categories, setCategories] = useState<
		{ categoryName: { name: string }; productCount: number }[]
	>([]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				setLoading(true);
				const data = await getAllCategory();
				setCategories(data?.data || []);
			} catch (error) {
				console.error('Failed to fetch categories:', error);
			} finally {
				setLoading(false);
			}
		};
		fetchCategories();
	}, []);

	useEffect(() => {
		if (product && categories.length > 0) {
			const validCategory = categories.some(
				(category) => category.categoryName.name === product.category
			)
				? product?.category
				: ''; // Set to '' if the category is invalid

			setFormData((prevFormData) => ({
				...prevFormData,
				_id: product._id || '',
				name: product.name || '',
				description: product.description || '',
				category: validCategory,
				price: product.price || '',
				quantity: product.quantity || '',
				bestSelling: product.bestSelling || false,
				trending: product.trending || false,
				productImage: product.productImage || [],
				weight: product.weight || '',
				weightUnit: product.weightUnit || 'kg',
				slug: product.slug || '',
				isInquiry: product.isInquiry || false,
				isBulkDiscount: product.isBulkDiscount || false,
				isCustomized: product.isCustomized || false,
				isWholesale: product.isWholesale || false,
				wholesaleFields: product.wholesaleFields || [],
				buldDiscountsData: product.buldDiscountsData || [],
				wholesaleMinQuantity: product.wholesaleMinQuantity || 1,
				productId: product.productId || '',
			}));
			// Set image previews if product images exist
			setImagePreviews(
				product.productImage && product.productImage.length > 0
					? product.productImage.map((value) => value as string)
					: []
			);
		}

		setFormData({ ...formData, slug: product?.name });
	}, [product, categories]);
	console.log({ ...formData, slug: product?.name }, 'demo');

	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
	) => {
		const { name, value } = e.target;
		const checked = (e.target as HTMLInputElement).checked;

		setFormData((prevFormData) => {
			const newFormData = {
				...prevFormData,
				[name]:
					e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
						? checked
						: value,
			};

			// Ensure dependent fields are reset when toggling certain switches
			if (name === 'isWholesale' || name === 'isBulkDiscount') {
				newFormData.isInquiry = false;
			}
			if (name === 'isWholesale' && !checked) {
				newFormData.wholesaleFields = [];
			}
			if (name === 'isBulkDiscount' && !checked) {
				newFormData.buldDiscountsData = [];
			}

			return newFormData;
		});
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files ? Array.from(e.target.files) : [];

		setNewProductImage((prevNewProductImage) => [...prevNewProductImage, ...files]);
		setImagePreviews((prevPreviews) => [
			...prevPreviews,
			...files.map((file) => URL.createObjectURL(file)),
		]);
	};

	const handleRemoveImage = async (index: number, imageUrl: string | File) => {
		if (typeof imageUrl === 'string' && formData.productId) {
			// Call API to remove the image from the backend
			const data = { imageUrl: imageUrl };
			const response = await deleteProductImage(formData.productId, data);
			if (response.success) {
				toast.success('Image removed successfully!');
			} else {
				toast.error('Failed to remove image.');
				return;
			}
		} else {
			// Remove the image from the newProductImage array
			setNewProductImage((prevNewProductImage) =>
				prevNewProductImage.filter((_, i) => i !== index)
			);
		}

		// Remove the image from the formData.productImage array
		const newImages = formData.productImage.filter((_, i) => i !== index);
		const newPreviews = imagePreviews.filter((_, i) => i !== index);
		setFormData({
			...formData,
			productImage: newImages,
		});
		setImagePreviews(newPreviews);
	};

	const handleWholeSaleFieldChange = (index: number, field: keyof wholesale, value: any) => {
		const newWholesaleFields = [...formData.wholesaleFields];
		switch (field) {
			case 'required':
				newWholesaleFields[index].required = value as boolean;
				break;
			case 'label':
				newWholesaleFields[index].label = value as string;
				break;
			case 'type':
				newWholesaleFields[index].type = value as 'text' | 'image';
				break;
			default:
				break;
		}
		setFormData({
			...formData,
			wholesaleFields: newWholesaleFields,
		});
	};

	const handleBulkDiscountFieldChange = (
		index: number,
		field: keyof buldDiscount,
		value: any
	) => {
		const newBulkDiscountsData = [...formData.buldDiscountsData];
		if (field === 'maxQuantity' && Number(value) <= newBulkDiscountsData[index].minQuantity) {
			toast.error('Maximum Quantity must be greater than Minimum Quantity');
			return;
		}
		newBulkDiscountsData[index][field] = value;
		setFormData({
			...formData,
			buldDiscountsData: newBulkDiscountsData,
		});
	};

	const handleAddWholeSaleField = () => {
		if (customizedField.label.trim().length === 0) {
			setError((prevError) => ({
				...prevError,
				customizedLabelError: true, // Set error if the required field is empty
			}));
			return;
		}

		setFormData((prevFormData) => ({
			...prevFormData,
			wholesaleFields: [...prevFormData.wholesaleFields, customizedField],
		}));

		// Reset the customizedField state after successful addition
		setCustomizedField({
			type: 'text',
			label: '',
			required: false,
		});

		// Reset error state after successful addition
		setError((prevError) => ({
			...prevError,
			customizedLabelError: false,
		}));
	};

	const handleAddBulkDiscountField = () => {
		setFormData({
			...formData,
			buldDiscountsData: [...formData.buldDiscountsData, bulkDiscountField],
		});
		setBulkDiscountField({ minQuantity: 1, maxQuantity: 1, discountPercentage: 1 });
	};

	const handleDeleteWholeSaleField = (index: number) => {
		const newWholesaleFields = formData.wholesaleFields.filter((_, i) => i !== index);
		setFormData({
			...formData,
			wholesaleFields: newWholesaleFields,
		});
	};

	const handleDeleteBulkDiscountField = (index: number) => {
		const newBulkDiscountsData = formData.buldDiscountsData.filter((_, i) => i !== index);
		setFormData({
			...formData,
			buldDiscountsData: newBulkDiscountsData,
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (newProductImage.length === 0 && !product?.slug) {
			toast.error('Please upload at least one image.');
			return;
		}
		const formDataToSend = new FormData();
		formDataToSend.append('name', formData.name);
		formDataToSend.append('slug', formData?.slug || '');
		formDataToSend.append('description', formData.description);
		formDataToSend.append('category', formData.category);
		formDataToSend.append('trending', String(formData.trending));
		newProductImage.forEach((file) => {
			formDataToSend.append('productImage', file);
		});
		if (!formData.isInquiry) {
			formDataToSend.append('weight', formData.weight);
			formDataToSend.append('price', formData.price.toString());
			formDataToSend.append('quantity', formData.quantity);
			formDataToSend.append('bestSelling', String(formData.bestSelling));
			if (formData.isWholesale) {
				formDataToSend.append(
					'wholesaleMinQuantity',
					String(formData.wholesaleMinQuantity)
				);
			}
			formDataToSend.append('weightUnit', formData.weightUnit);
			formDataToSend.append('isBulkDiscount', String(formData.isBulkDiscount));
			formDataToSend.append('isCustomized', String(formData.isCustomized));
			formDataToSend.append('isWholesale', String(formData.isWholesale));
		}
		formDataToSend.append('isInquiry', String(formData.isInquiry));
		formData.wholesaleFields.forEach((field, index) => {
			formDataToSend.append(`wholesaleFields[${index}][type]`, field.type);
			formDataToSend.append(`wholesaleFields[${index}][label]`, field.label);
			formDataToSend.append(`wholesaleFields[${index}][required]`, String(field.required));
		});
		formData.buldDiscountsData.forEach((discount, index) => {
			formDataToSend.append(
				`bulkDiscounts[${index}][minQuantity]`,
				String(discount.minQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][maxQuantity]`,
				String(discount.maxQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][discountPercentage]`,
				String(discount.discountPercentage)
			);
		});

		try {
			setLoading(true);
			if (product && product.slug) {
				// const response = await updateProduct(formDataToSend, product.slug);
				const response = await updateProduct(formDataToSend, product._id);
				if (response.success) {
					fetchData();
					openDialog();
					toast.success('Product updated successfully!');
				} else {
					toast.error(response.data.errors);
				}
			} else {
				const response = await addProduct(formDataToSend);
				if (response.isError) {
					toast.error(response.data.errors);
				} else {
					toast.success('Product added successfully!');
					openDialog();
					fetchData();
				}
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('Failed to save product.');
		} finally {
			setLoading(false);
		}
	};

	const handleWholesaleToggle = (e: ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setFormData((prevFormData) => ({
			...prevFormData,
			isWholesale: checked,
			wholesaleFields: checked ? prevFormData.wholesaleFields : [],
		}));
	};
	const handleBulkDiscountToggle = (e: ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setFormData((prevFormData) => ({
			...prevFormData,
			isBulkDiscount: checked,
			buldDiscountsData: checked ? prevFormData.buldDiscountsData : [],
		}));
	};
	const handleIsCustomizedToggle = (e: ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setFormData((prevFormData) => ({
			...prevFormData,
			isCustomized: checked,
			wholesaleFields: checked ? prevFormData.wholesaleFields : [],
		}));
	};

	const quillRef = useRef(null); // Add a ref for ReactQuill

	return (
		<div>
			<form onSubmit={handleSubmit}>
				<Grid container spacing={2}>
					<Grid size={{ xs: 12 }}>
						<TextField
							size="small"
							label="Product Name"
							name="name"
							variant="outlined"
							fullWidth
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							required
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<TextField
							size="small"
							name="slug"
							label="URL Name"
							variant="outlined"
							fullWidth
							value={formData.slug}
							onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
							required
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<ReactQuill
							ref={quillRef} // Attach the ref to ReactQuill
							value={formData.description}
							onChange={(value) => setFormData({ ...formData, description: value })}
							modules={{
								toolbar: [
									[{ header: '1' }, { header: '2' }, { font: [] }],
									[{ size: [] }],
									['bold', 'italic', 'underline', 'strike', 'blockquote'],
									[
										{ list: 'ordered' },
										{ list: 'bullet' },
										{ indent: '-1' },
										{ indent: '+1' },
									],
									['link', 'image', 'video'],
									['clean'],
								],
							}}
							formats={[
								'header',
								'font',
								'size',
								'bold',
								'italic',
								'underline',
								'strike',
								'blockquote',
								'list',
								'bullet',
								'indent',
								'link',
								'image',
								'video',
							]}
						/>
						<input
							type="text"
							name="content"
							value={formData.description}
							required
							style={{ visibility: 'hidden' }}
							readOnly
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<FormControl fullWidth margin="normal" size="small" sx={{ margin: '0px' }}>
							<InputLabel id="category">Category</InputLabel>
							<Select
								label="Category"
								name="category"
								id="category"
								value={formData.category}
								onChange={handleChange}
								required
								labelId="category"
							>
								<MenuItem value="">Select Category</MenuItem>
								{categories?.map((category, index) => (
									<MenuItem key={index} value={category.categoryName.name}>
										{category.categoryName.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							sx={{ margin: '0px' }}
							control={
								<Switch
									checked={formData.isInquiry}
									onChange={(e) =>
										setFormData({ ...formData, isInquiry: e.target.checked })
									}
									disabled={formData.isWholesale || formData.isBulkDiscount}
								/>
							}
							label="Is Enquiry"
						/>
					</Grid>

					{!formData.isInquiry && (
						<>
							<Grid size={{ xs: 12 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Stock"
									name="quantity"
									type="number"
									value={formData.quantity}
									onChange={handleChange}
									required
									fullWidth
									margin="normal"
									inputProps={{ min: '1' }}
								/>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<FormControlLabel
									control={
										<Checkbox
											name="bestSelling"
											checked={formData.bestSelling}
											onChange={handleChange}
										/>
									}
									label="Best Selling"
								/>
								<FormControlLabel
									control={
										<Checkbox
											name="trending"
											checked={formData.trending}
											onChange={handleChange}
										/>
									}
									label="Trending"
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Product Price"
									variant="outlined"
									type="number"
									fullWidth
									value={formData.price}
									// onChange={(e) =>
									// 	setFormData({ ...formData, price: Number(e.target.value) })
									// }
									onChange={handleChange}
									inputProps={{ min: '1' }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Weight"
									name="weight"
									type="number"
									value={formData.weight}
									onChange={handleChange}
									required
									fullWidth
									inputProps={{ min: '1' }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								{' '}
								<FormControl fullWidth required>
									<InputLabel id="weightUnit-label">Weight Unit</InputLabel>
									<Select
										size="small"
										labelId="weightUnit-label"
										id="weightUnit"
										name="weightUnit"
										value={formData.weightUnit}
										onChange={handleChange}
										label="Weight Unit"
									>
										<MenuItem value="kg">kg</MenuItem>
										<MenuItem value="g">g</MenuItem>
										<MenuItem value="lb">lb</MenuItem>
										<MenuItem value="oz">oz</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</>
					)}

					{!formData.isInquiry && (
						<Grid size={{ xs: 12 }}>
							<FormControlLabel
								sx={{ margin: '0px' }}
								control={
									<Switch
										checked={formData.isWholesale}
										onChange={(e) => handleWholesaleToggle(e)}
									/>
								}
								label="Is Wholesale"
							/>
							<FormControlLabel
								sx={{ margin: '0px' }}
								control={
									<Switch
										checked={formData.isBulkDiscount}
										onChange={(e) => handleBulkDiscountToggle(e)}
									/>
								}
								label="Is Bulk Discount"
							/>
						</Grid>
					)}
					{!formData.isInquiry && (
						<>
							{formData.isWholesale && (
								<>
									<Grid size={{ xs: 12 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Wholesale Minimum Order Quantity"
											name="wholesaleMinQuantity"
											type="number"
											value={formData.wholesaleMinQuantity}
											onChange={handleChange}
											fullWidth
											margin="normal"
											inputProps={{ min: '1' }}
										/>
									</Grid>
									<Grid size={{ xs: 12 }}>
										<FormControlLabel
											sx={{ margin: '0px' }}
											control={
												<Switch
													checked={formData.isCustomized}
													onChange={(e) => handleIsCustomizedToggle(e)}
												/>
											}
											label="Is Customized"
										/>
									</Grid>
								</>
							)}

							{formData?.isCustomized &&
								formData?.isWholesale &&
								formData?.wholesaleFields?.length > 0 &&
								formData?.wholesaleFields?.map((value, index) => (
									<React.Fragment key={index}>
										<Grid size={{ xs: 12, md: 4 }}>
											<FormControl fullWidth>
												<InputLabel id={`wholesale-type-${index}`}>
													Field Type
												</InputLabel>
												<Select
													size="small"
													labelId={`wholesale-type-${index}`}
													id={`wholesale-type-id-${index}`}
													value={value.type}
													label="Field Type"
													onChange={(e) =>
														handleWholeSaleFieldChange(
															index,
															'type',
															e.target.value
														)
													}
												>
													<MenuItem value="text">Text</MenuItem>
													<MenuItem value="image">Image</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid size={{ xs: 12, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Label"
												name="label"
												type="text"
												value={value.label}
												onChange={(e) =>
													handleWholeSaleFieldChange(
														index,
														'label',
														e.target.value
													)
												}
												required
												fullWidth
												margin="normal"
											/>
										</Grid>
										<Grid size={{ xs: 9, sm: 9, md: 3 }}>
											<FormControlLabel
												sx={{ margin: '0px' }}
												control={
													<Switch
														checked={value.required}
														onChange={(e) =>
															handleWholeSaleFieldChange(
																index,
																'required',
																e.target.checked
															)
														}
													/>
												}
												label="Is Required"
											/>
										</Grid>
										<Grid size={{ xs: 3, sm: 3, md: 1 }}>
											<IconButton
												aria-label="Delete"
												onClick={() => handleDeleteWholeSaleField(index)}
											>
												<DeleteIcon />
											</IconButton>
										</Grid>
									</React.Fragment>
								))}
							{formData.isCustomized && formData.isWholesale && (
								<React.Fragment>
									<Grid size={{ xs: 12, sm: 6, md: 4 }}>
										<FormControl fullWidth>
											<InputLabel id={`wholesale-type`}>
												Field Type
											</InputLabel>
											<Select
												size="small"
												labelId={`wholesale-type`}
												id={`wholesale-type-id-`}
												value={customizedField.type}
												label="Field Type"
												onChange={(e) =>
													setCustomizedField({
														...customizedField,
														type: e.target.value as 'text' | 'image',
													})
												}
											>
												<MenuItem value="text">Text</MenuItem>
												<MenuItem value="image">Image</MenuItem>
											</Select>
										</FormControl>
									</Grid>
									<Grid size={{ xs: 12, sm: 6, md: 4 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Label"
											name="label"
											type="text"
											value={customizedField.label}
											onChange={(e) => {
												setCustomizedField({
													...customizedField,
													label: e.target.value,
												});

												// Reset error when user starts typing
												if (
													error.customizedLabelError &&
													e.target.value.trim() !== ''
												) {
													setError((prevError) => ({
														...prevError,
														customizedLabelError: false,
													}));
												}
											}}
											fullWidth
											margin="normal"
										/>
									</Grid>
									<Grid size={{ xs: 9, sm: 9, md: 3 }}>
										<FormControlLabel
											sx={{ margin: '0px' }}
											control={
												<Switch
													checked={customizedField.required}
													onChange={(e) =>
														setCustomizedField({
															...customizedField,
															required: e.target.checked,
														})
													}
												/>
											}
											label="Is Required"
										/>
									</Grid>
									<Grid size={{ xs: 3, sm: 3, md: 1 }}>
										<IconButton
											aria-label="Add"
											onClick={handleAddWholeSaleField}
										>
											<AddCircleIcon />
										</IconButton>
									</Grid>
								</React.Fragment>
							)}

							{/* {formData.isBulkDiscount && formData.buldDiscountsData.length > 0 && (
								<Divider />
							)} */}

							{formData.isBulkDiscount &&
								formData.buldDiscountsData.length > 0 &&
								formData.buldDiscountsData.map((val, index) => (
									<React.Fragment key={index}>
										<Grid size={{ xs: 12, sm: 6, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Minimum Quantity"
												name="minQuantity"
												type="number"
												value={val.minQuantity}
												onChange={(e) =>
													handleBulkDiscountFieldChange(
														index,
														'minQuantity',
														e.target.value
													)
												}
												required
												fullWidth
												margin="normal"
												inputProps={{ min: '1' }}
											/>
										</Grid>
										<Grid size={{ xs: 12, sm: 6, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Maximum Quantity"
												name="maxQuantity"
												type="number"
												value={val.maxQuantity}
												onChange={(e) =>
													handleBulkDiscountFieldChange(
														index,
														'maxQuantity',
														e.target.value
													)
												}
												required
												fullWidth
												margin="normal"
												inputProps={{ min: '1' }}
											/>
										</Grid>
										<Grid size={{ xs: 9, sm: 9, md: 3 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Discount Percentage"
												name="discountPercentage"
												type="number"
												value={val.discountPercentage}
												onChange={(e) =>
													handleBulkDiscountFieldChange(
														index,
														'discountPercentage',
														e.target.value
													)
												}
												required
												fullWidth
												margin="normal"
												inputProps={{ min: '1' }}
											/>
										</Grid>
										<Grid size={{ xs: 3, sm: 3, md: 4 }}>
											<IconButton
												aria-label="Delete"
												onClick={() => handleDeleteBulkDiscountField(index)}
											>
												<DeleteIcon />
											</IconButton>
										</Grid>
									</React.Fragment>
								))}
							{formData.isBulkDiscount && (
								<React.Fragment>
									<Grid size={{ xs: 12, sm: 6, md: 4 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Minimum Quantity"
											name="minQuantity"
											type="number"
											value={bulkDiscountField.minQuantity}
											onChange={(e) =>
												setBulkDiscountField({
													...bulkDiscountField,
													minQuantity: Number(e.target.value),
												})
											}
											required
											fullWidth
											margin="normal"
											inputProps={{ min: '1' }}
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 6, md: 4 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Maximum Quantity"
											name="maxQuantity"
											type="number"
											value={bulkDiscountField.maxQuantity}
											onChange={(e) =>
												setBulkDiscountField({
													...bulkDiscountField,
													maxQuantity: Number(e.target.value),
												})
											}
											required
											fullWidth
											margin="normal"
											inputProps={{ min: '1' }}
										/>
									</Grid>
									<Grid size={{ xs: 9, sm: 9, md: 3 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Discount Percentage"
											name="discountPercentage"
											type="number"
											value={bulkDiscountField.discountPercentage}
											onChange={(e) =>
												setBulkDiscountField({
													...bulkDiscountField,
													discountPercentage: Number(e.target.value),
												})
											}
											required
											fullWidth
											margin="normal"
											inputProps={{ min: '1' }}
										/>
									</Grid>
									<Grid size={{ xs: 3, sm: 3, md: 1 }}>
										<IconButton
											aria-label="Add"
											onClick={handleAddBulkDiscountField}
										>
											<AddCircleIcon />
										</IconButton>
									</Grid>
								</React.Fragment>
							)}
						</>
					)}
					<Grid size={{ xs: 12 }}>
						<div style={{ marginBottom: '12px' }}>
							<input
								type="file"
								id="productImages"
								name="productImages"
								onChange={handleFileChange}
								accept="image/jpeg, image/png, image/jpg"
								multiple
								style={{ display: 'none' }}
							/>
							<label htmlFor="productImages">
								<Button variant="contained" color="primary" component="span">
									Upload Images
								</Button>
							</label>
							<div>
								{imagePreviews.map((src, index) => (
									<div
										key={index}
										style={{
											display: 'inline-block',
											position: 'relative',
											margin: '5px',
										}}
									>
										<img
											src={src}
											alt={`Preview ${index}`}
											style={{ width: '100px', height: '100px' }}
										/>
										<IconButton
											style={{ position: 'absolute', top: '0', right: '0' }}
											onClick={() => handleRemoveImage(index, src)}
										>
											<DeleteIcon />
										</IconButton>
									</div>
								))}
							</div>
						</div>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							fullWidth
							loading={loading}
							loadingIndicator="Loading…"
						>
							{product?.slug ? 'Update Product' : 'Create Product'}
						</Button>
					</Grid>
				</Grid>
			</form>
		</div>
	);
};

export default CreateProducts;
